import { ReactNode, createContext } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { LoginRequest, User } from '$api-client/types.gen'
import { authLoginMutation, authLogOutMutation, authUserOptions, authUserQueryKey } from '$api-client/@tanstack/react-query.gen'
import { httpClient } from 'src/config/http-client'
import { QueryError, QueryErrorHelper } from '$utils/query'

export type AuthContext = {
  user: User,
  isLoggedIn: boolean,
  isLoading: boolean,
  isSignInLoading: boolean,
  signOut: () => void,
  signIn: (credentials: LoginRequest) => Promise<void>,
  signInOAuth: (provider: 'google' | 'facebook') => void,
  refetchUser: () => void,
  error: QueryError | null,
}

export const AuthContext = createContext<AuthContext>({
  user: null!,
  isLoggedIn: false,
  isLoading: true,
  isSignInLoading: false,
  signOut: () => {},
  signIn: () => Promise.resolve(),
  signInOAuth: () => {},
  refetchUser: () => {},
  error: null,
})

export type AuthProviderProps = {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient()

  const { data: user, isLoading: isUserLoading, refetch: refetchUser } = useQuery({
    ...authUserOptions(),
    retry: false,
  })

  const loginMutation = useMutation({
    ...authLoginMutation(),
    onSuccess: (data) => queryClient.setQueryData(authUserQueryKey(), data),
  })

  const logoutMutation = useMutation({ ...authLogOutMutation() })

  const signIn = async (credentials: LoginRequest) => {
    await loginMutation.mutateAsync({
      body: credentials,
      client: httpClient,
    })
  }

  const signInOAuth = (provider: 'google' | 'facebook') => window.location.href = `${import.meta.env.VITE_API_BASE_URL}/auth/${provider}/redirect`

  const signOut = async () => {
    await logoutMutation.mutateAsync({
    })

    queryClient.removeQueries()
  }

  const getAuthError = (): QueryError | null => {
    if (!loginMutation.error) {
      return null
    }

    // Handle authentication-specific errors
    const error = loginMutation.error

    // Try to extract error message from various possible structures
    if (typeof error === 'object' && error !== null) {
      // Check for direct message property (common in API responses)
      if ('message' in error && typeof error.message === 'string') {
        return {
          message: error.message,
          errors: {},
        }
      }

      // Check for status 401 with message
      if ('status' in error && error.status === 401 && 'body' in error) {
        const body = error.body as Record<string, unknown>
        if (body && typeof body.message === 'string') {
          return {
            message: body.message,
            errors: {},
          }
        }
      }
    }

    // Fallback to QueryErrorHelper
    return QueryErrorHelper.getQueryError(error)
  }

  const context: AuthContext = {
    user: user?.data as User,
    isLoggedIn: !!user?.data,
    isLoading: isUserLoading,
    signOut,
    signIn,
    signInOAuth,
    refetchUser,
    isSignInLoading: loginMutation.isPending,
    error: getAuthError(),
  }

  return (
    <AuthContext.Provider value={context}>
      {!isUserLoading && children}
    </AuthContext.Provider>
  )
}
